// =====================================================================================
// INVRT Radar - web console - the company journey + ICP roster client (V03)
//
// The browser client for the V03 seam (routes/company_journey.ts): GET /companies/journey
// and GET /companies/roster. The types.radar.ts law holds: the parsers below describe
// exactly what the two routes return and nothing more; a response that is structurally
// nonsense fails closed as an ApiError instead of rendering half a page.
//
// The SEPARATION is pinned at parse time, not just in the UI: a journey payload whose
// timeline carries a roster-shaped row is refused, and a roster payload carrying a
// visitor-shaped row (a visit_key, an event_at, a late flag, or an id outside the rev_
// namespace) is refused. A roster contact of the same company can therefore never render
// as, or inside, the visitor journey in this console - even if a future server regression
// tried to merge the two surfaces.
// =====================================================================================

import { ApiError } from "@/lib/api";
import type { useApiClient } from "@/hooks/useApi";

type ApiClient = ReturnType<typeof useApiClient>;

// ---- shapes -----------------------------------------------------------------------------

export interface JourneyVisitRow {
  id: string;
  kind: "visit";
  /** EVENT time - when the visit happened, not when the delivery arrived. */
  event_at: string;
  arrived_at: string | null;
  late: boolean;
  arrival_delay_ms: number | null;
  visit_number: number;
  visit_key: string | null;
  identified_level: "person" | "company";
  person: Record<string, unknown> | null;
  company: Record<string, unknown> | null;
  pages: unknown[];
  campaign: unknown;
  is_known_customer: boolean;
  site_id: string | null;
}

export interface RosterContactRow {
  id: string;
  kind: "roster_contact";
  source: "revealed";
  occurred_at: string;
  revealed_at: string | null;
  person: Record<string, unknown> | null;
  company: Record<string, unknown> | null;
}

export interface JourneyHonesty {
  state: "complete" | "partial" | "unavailable";
  reason: string | null;
}

export interface CompanyJourneyResponse {
  company_key: { domain?: string; name?: string };
  company: Record<string, unknown> | null;
  visits: JourneyVisitRow[];
  totals: {
    events: number;
    distinct_visits: number;
    pages_viewed: number;
    first_event_at: string | null;
    last_event_at: string | null;
  };
  honesty: JourneyHonesty;
}

export interface CompanyRosterResponse {
  company_key: { domain?: string; name?: string };
  contacts: RosterContactRow[];
  honesty: JourneyHonesty;
}

export interface CompanyQuery {
  domain?: string;
  name?: string;
  limit?: number;
}

// ---- fail-closed parsing primitives (the house idiom) -----------------------------------

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const BAD_CONTRACT = "The company journey response did not match the reviewed contract.";

function invalidContract(): ApiError {
  return new ApiError({
    token: "internal_error",
    message: BAD_CONTRACT,
    httpStatus: 502,
    correlationId: null,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  return actual.length === keys.length && keys.every((key) => actual.includes(key));
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isDateTime(value: unknown): value is string {
  return typeof value === "string" && DATE_TIME_PATTERN.test(value);
}

function isNullableDateTime(value: unknown): value is string | null {
  return value === null || isDateTime(value);
}

function isPersonOrCompanyMap(value: unknown): value is Record<string, unknown> | null {
  return value === null || isRecord(value);
}

const HONESTY_STATES = ["complete", "partial", "unavailable"] as const;

function isHonesty(value: unknown): value is JourneyHonesty {
  if (!isRecord(value) || !hasExactKeys(value, ["state", "reason"])) return false;
  if (!HONESTY_STATES.includes(value.state as JourneyHonesty["state"])) return false;
  return value.reason === null || typeof value.reason === "string";
}

// ---- the journey payload ----------------------------------------------------------------

const VISIT_KEYS = [
  "id",
  "kind",
  "event_at",
  "arrived_at",
  "late",
  "arrival_delay_ms",
  "visit_number",
  "visit_key",
  "identified_level",
  "person",
  "company",
  "pages",
  "campaign",
  "is_known_customer",
  "site_id",
] as const;

function isVisitRow(value: unknown): value is JourneyVisitRow {
  if (!isRecord(value) || !hasExactKeys(value, VISIT_KEYS)) return false;
  if (!isUuid(value.id) || value.kind !== "visit") return false;
  if (!isDateTime(value.event_at) || !isNullableDateTime(value.arrived_at)) return false;
  if (typeof value.late !== "boolean") return false;
  if (value.arrival_delay_ms !== null && typeof value.arrival_delay_ms !== "number") return false;
  if (typeof value.visit_number !== "number" || !Number.isInteger(value.visit_number)) return false;
  if (value.visit_key !== null && typeof value.visit_key !== "string") return false;
  if (value.identified_level !== "person" && value.identified_level !== "company") return false;
  if (!isPersonOrCompanyMap(value.person) || !isPersonOrCompanyMap(value.company)) return false;
  if (!Array.isArray(value.pages)) return false;
  if (typeof value.is_known_customer !== "boolean") return false;
  if (value.site_id !== null && !isUuid(value.site_id)) return false;
  return true;
}

export function parseCompanyJourney(value: unknown): CompanyJourneyResponse {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["company_key", "company", "visits", "totals", "honesty"])
  ) {
    throw invalidContract();
  }
  if (!isRecord(value.company_key)) throw invalidContract();
  if (!isPersonOrCompanyMap(value.company)) throw invalidContract();
  if (!Array.isArray(value.visits) || !value.visits.every(isVisitRow)) throw invalidContract();
  const totals = value.totals;
  if (!isRecord(totals)) throw invalidContract();
  if (
    !hasExactKeys(totals, ["events", "distinct_visits", "pages_viewed", "first_event_at", "last_event_at"]) ||
    typeof totals.events !== "number" ||
    typeof totals.distinct_visits !== "number" ||
    typeof totals.pages_viewed !== "number" ||
    !isNullableDateTime(totals.first_event_at) ||
    !isNullableDateTime(totals.last_event_at)
  ) {
    throw invalidContract();
  }
  if (!isHonesty(value.honesty)) throw invalidContract();
  const companyKey: CompanyJourneyResponse["company_key"] = {};
  if (value.company_key.domain !== undefined) {
    if (typeof value.company_key.domain !== "string") throw invalidContract();
    companyKey.domain = value.company_key.domain;
  }
  if (value.company_key.name !== undefined) {
    if (typeof value.company_key.name !== "string") throw invalidContract();
    companyKey.name = value.company_key.name;
  }
  return {
    company_key: companyKey,
    company: value.company,
    visits: value.visits,
    totals: {
      events: totals.events,
      distinct_visits: totals.distinct_visits,
      pages_viewed: totals.pages_viewed,
      first_event_at: totals.first_event_at,
      last_event_at: totals.last_event_at,
    },
    honesty: value.honesty,
  };
}

// ---- the roster payload -----------------------------------------------------------------

const ROSTER_KEYS = [
  "id",
  "kind",
  "source",
  "occurred_at",
  "revealed_at",
  "person",
  "company",
] as const;

function isRosterRow(value: unknown): value is RosterContactRow {
  if (!isRecord(value) || !hasExactKeys(value, ROSTER_KEYS)) return false;
  if (typeof value.id !== "string" || !value.id.startsWith("rev_")) return false;
  if (!isUuid(value.id.slice(4))) return false;
  if (value.kind !== "roster_contact" || value.source !== "revealed") return false;
  if (!isDateTime(value.occurred_at) || !isNullableDateTime(value.revealed_at)) return false;
  if (!isPersonOrCompanyMap(value.person) || !isPersonOrCompanyMap(value.company)) return false;
  return true;
}

export function parseCompanyRoster(value: unknown): CompanyRosterResponse {
  if (!isRecord(value) || !hasExactKeys(value, ["company_key", "contacts", "honesty"])) {
    throw invalidContract();
  }
  if (!isRecord(value.company_key)) throw invalidContract();
  if (!Array.isArray(value.contacts) || !value.contacts.every(isRosterRow)) throw invalidContract();
  if (!isHonesty(value.honesty)) throw invalidContract();
  const companyKey: CompanyRosterResponse["company_key"] = {};
  if (value.company_key.domain !== undefined) {
    if (typeof value.company_key.domain !== "string") throw invalidContract();
    companyKey.domain = value.company_key.domain;
  }
  if (value.company_key.name !== undefined) {
    if (typeof value.company_key.name !== "string") throw invalidContract();
    companyKey.name = value.company_key.name;
  }
  return {
    company_key: companyKey,
    contacts: value.contacts,
    honesty: value.honesty,
  };
}

// ---- the two seam calls -----------------------------------------------------------------

export async function fetchCompanyJourney(
  api: ApiClient,
  query: CompanyQuery,
): Promise<CompanyJourneyResponse> {
  const response = await api.get<unknown>("/companies/journey", {
    query: buildQuery(query),
  });
  return parseCompanyJourney(response);
}

export async function fetchCompanyRoster(
  api: ApiClient,
  query: CompanyQuery,
): Promise<CompanyRosterResponse> {
  const response = await api.get<unknown>("/companies/roster", {
    query: buildQuery(query),
  });
  return parseCompanyRoster(response);
}

function buildQuery(query: CompanyQuery): Record<string, string> {
  const params: Record<string, string> = {};
  if (query.domain !== undefined) params.domain = query.domain;
  if (query.name !== undefined) params.name = query.name;
  if (query.limit !== undefined) params.limit = String(query.limit);
  return params;
}
